import React, { Suspense, useCallback, useEffect, useRef } from 'react';
import { pick, prop } from 'ramda';
import { dispatcher } from 'dispatcher';
import { publicEventsAPIService } from '@core/js/services/publicEventsAPI.service';
import { useDeviceSelector, useUserSelector } from '@core/js/store/hooks';
import { useToggleBalance } from '@core/js/hooks/user';
import { useComponentTranslations } from '@core/js/hooks/component';
import { useOnLogoutButtonClick } from '@theme/js/components/main/LogoutButton/hooks';

import { BackboneReplacer } from '@core/js/backbone/components/BackboneReplacer';
import { Icon } from '@core/js/components/common/Icon';
import { Balance } from '@core/js/components/main/Balance';
import { AccessibilityElement } from '@core/js/components/common/AccessibilityElement';
import { AccountPanelDetails } from './../AccountPanelDetails';
import { AccountPanelTotal } from './../AccountPanelTotal';

import { useStickyFooterHeight } from './hooks';
import { TAccountPanelProps } from './types';
import style from './AccountPanel.scss';

export const AccountPanel: React.FC<TAccountPanelProps> = ({
    headerHeight = 0
}) => {
    const translate = useComponentTranslations();
    const { hideBalance, userDetails } = useUserSelector(pick(['hideBalance', 'userDetails']));
    const isMobile = useDeviceSelector(prop('isMobile'));
    const toggleBalance = useToggleBalance();
    const accountPanelRef = useRef<HTMLDivElement>(null);

    const onClickLogout = useOnLogoutButtonClick();
    const onToggleBalance = useCallback(() => toggleBalance(!hideBalance), [toggleBalance, hideBalance]);
    const onClick = useCallback(() => dispatcher.dispatch('user:toggleBalance'), []);

    const stickyFooterHeight = useStickyFooterHeight();
    const setDynamicHeight = useCallback(() => {
        if (accountPanelRef.current && isMobile) {
            const maxHeight = window.innerHeight - headerHeight - stickyFooterHeight;
            accountPanelRef.current.style.maxHeight = `${maxHeight}px`;
        }
    }, [headerHeight, isMobile, stickyFooterHeight]);

    useEffect(() => {
        if (!isMobile) { return; }

        setDynamicHeight();

        window.addEventListener('resize', setDynamicHeight);
        return () => window.removeEventListener('resize', setDynamicHeight);
    }, [isMobile, setDynamicHeight]);

    useEffect(() => {
        // Propagate from React to Backbone
        dispatcher.dispatch('user:toggleBalance');
        // Propagate to listen from admin (e.g. WC, tags)
        publicEventsAPIService.trigger('user:toggleBalance', !hideBalance);
    }, [hideBalance]);

    return (
        <div
            className={style.accountPanel}
            {...(isMobile && { ref: accountPanelRef })}
        >
            <div className={style.balancePanelSummary}>
                <div className={style.userPanel}>
                    <span className={style.userName}>{userDetails.loginName}</span>
                    <span className={style.balanceTitle}>
                        {translate('header.account-panel.balances.title')}
                    </span>
                    {!hideBalance && (
                        <span className={style.balanceAmount}>
                             <Suspense fallback="">
                                 <Balance type="real_gaming_balance" />
                             </Suspense>
                        </span>
                    )}
                </div>

                <div className={style.buttonsPanel}>
                    <AccessibilityElement onClick={onToggleBalance} className={style.balanceIcon}>
                        <Icon
                            type={hideBalance ? 'headerPanelBalanceHide' : 'headerPanelBalanceShow'}
                            className={`${style.icon} ${hideBalance ? 'cms-header-panel-balance-icon-hide' : 'cms-header-panel-balance-icon-show'}`}
                        />
                    </AccessibilityElement>

                    <AccessibilityElement onClick={onClick} className={style.refreshIcon}>
                        <Icon
                            type='headerPanelBalanceRefresh'
                            className={`${style.icon} cms-header-panel-balance-refresh-icon`}
                        />
                    </AccessibilityElement>

                    <div className={style.deposit}>
                        <BackboneReplacer dynamicName="HEADER_DEPOSIT_BUTTON" viewName="headerdepositbutton" args='accountPanelBtn'/>
                    </div>
                </div>
            </div>

            <AccountPanelDetails />
            <AccountPanelTotal />

            <div className={style.accountPanelLogout}>
                <button onClick={onClickLogout} className={style.logout}>
                    {translate('header.account-panel.logout.btn.title')}
                </button>
            </div>
        </div>
    );
};
